import React, { useState, useEffect } from 'react';
import { codeRepoAPI, categoryAPI, tagAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import CodeRepoForm from '../components/CodeRepoForm';

const CodeRepos = () => {
  const navigate = useNavigate();
  const [repos, setRepos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingRepo, setEditingRepo] = useState(null);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });

  useEffect(() => {
    fetchCategories();
    fetchTags();
  }, []);

  useEffect(() => {
    fetchRepos();
  }, [pagination.page, searchTerm, categoryFilter, levelFilter, activeFilter]);

  const fetchCategories = async () => {
    try {
      const response = await categoryAPI.getAllCategories();
      setCategories(response.data.categories || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await tagAPI.getAllTags();
      setTags(response.data.tags || []);
    } catch (err) {
      console.error('Error fetching tags:', err);
    }
  };

  const fetchRepos = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm,
        ...(categoryFilter && { category: categoryFilter }),
        ...(levelFilter && { level: levelFilter }),
        ...(activeFilter && { isActive: activeFilter })
      };
      
      const response = await codeRepoAPI.getAllRepos(params);
      setRepos(response.data.repos);
      setPagination(prev => ({
        ...prev,
        total: response.data.total,
        pages: response.data.pages
      }));
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching code repos');
      console.error('Error fetching repos:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this code repo?')) {
      return;
    }

    try {
      await codeRepoAPI.deleteRepo(id);
      setSuccess('Code repo deleted successfully');
      setTimeout(() => setSuccess(null), 3000);
      fetchRepos();
    } catch (err) {
      setError(err.response?.data?.message || 'Error deleting code repo');
      console.error('Error deleting repo:', err);
    }
  };

  const handleEdit = (repo) => {
    setEditingRepo(repo);
    setShowForm(true);
  };

  const handleClone = async (repo) => {
    if (!window.confirm(`Clone "${repo.title}"? A new repository will be created with all metadata and assignments, but without any group/student enrollments.`)) {
      return;
    }

    try {
      const response = await codeRepoAPI.cloneRepo(repo._id);
      setSuccess(`Repository cloned successfully as "${response.data.repo.title}"`);
      setTimeout(() => setSuccess(null), 5000);
      fetchRepos();
    } catch (err) {
      setError(err.response?.data?.message || 'Error cloning code repo');
      console.error('Error cloning repo:', err);
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingRepo(null);
    fetchRepos();
  };


  const handleBuilder = (repoId) => {
    navigate(`/admin/code-repo-builder/${repoId}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'beginner': return '#10b981';
      case 'intermediate': return '#f59e0b';
      case 'advanced': return '#ef4444';
      default: return '#6b7280';
    }
  };

  if (showForm) {
    return (
      <div className="container">
        <div style={{ marginBottom: '16px' }}>
          <button 
            className="btn btn-secondary" 
            onClick={() => {
              setShowForm(false);
              setEditingRepo(null);
            }}
            style={{ fontSize: '12px' }}
          >
            ← Back to Repositories
          </button>
        </div>
        <CodeRepoForm onSuccess={handleFormSuccess} repo={editingRepo} />
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card">
        <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ color: '#667eea', fontSize: '22px', fontWeight: '700', marginBottom: '6px' }}>
              📦 Code Repositories
            </h2>
            <p style={{ color: '#718096', fontSize: '13px' }}>
              Manage collections of coding assignments
            </p>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => setShowForm(true)}
            style={{ fontSize: '12px' }}
          >
            + Create New Repository
          </button>
        </div>

        {error && (
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#fee2e2',
            color: '#991b1b',
            borderRadius: '8px',
            marginBottom: '20px',
            fontSize: '13px'
          }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#d1fae5',
            color: '#065f46',
            borderRadius: '8px',
            marginBottom: '20px',
            fontSize: '13px'
          }}>
            {success}
          </div>
        )}

        {/* Search and Filter Controls */}
        <div style={{ marginBottom: '20px', display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ flex: '1 1 300px', position: 'relative' }}>
            <input
              type="text"
              placeholder="🔍 Search repositories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                fontSize: '13px',
                border: '1.5px solid #e2e8f0',
                borderRadius: '8px',
                outline: 'none',
                transition: 'all 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#667eea'}
              onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
            />
          </div>

          <Select
            value={categoryFilter ? categories.find(c => c._id === categoryFilter) ? { value: categoryFilter, label: categories.find(c => c._id === categoryFilter).name } : { value: '', label: 'All Categories' } : { value: '', label: 'All Categories' }}
            onChange={(option) => setCategoryFilter(option.value)}
            options={[
              { value: '', label: 'All Categories' },
              ...categories.map(cat => ({ value: cat._id, label: cat.name }))
            ]}
            menuPortalTarget={document.body}
            styles={{
              control: (base) => ({
                ...base,
                minHeight: '38px',
                minWidth: '150px',
                fontSize: '13px',
                borderColor: '#e2e8f0',
                borderWidth: '1.5px'
              }),
              option: (base) => ({
                ...base,
                fontSize: '13px'
              }),
              menuPortal: (base) => ({ ...base, zIndex: 1050 })
            }}
          />

          <Select
            value={levelFilter ? { value: levelFilter, label: levelFilter.charAt(0).toUpperCase() + levelFilter.slice(1) } : { value: '', label: 'All Levels' }}
            onChange={(option) => setLevelFilter(option.value)}
            options={[
              { value: '', label: 'All Levels' },
              { value: 'beginner', label: 'Beginner' },
              { value: 'intermediate', label: 'Intermediate' },
              { value: 'advanced', label: 'Advanced' }
            ]}
            menuPortalTarget={document.body}
            styles={{
              control: (base) => ({
                ...base,
                minHeight: '38px',
                minWidth: '150px',
                fontSize: '13px',
                borderColor: '#e2e8f0',
                borderWidth: '1.5px'
              }),
              option: (base) => ({
                ...base,
                fontSize: '13px'
              }),
              menuPortal: (base) => ({ ...base, zIndex: 1050 })
            }}
          />

          <Select
            value={activeFilter ? { value: activeFilter, label: activeFilter === 'true' ? 'Active' : 'Inactive' } : { value: '', label: 'All Status' }}
            onChange={(option) => setActiveFilter(option.value)}
            options={[
              { value: '', label: 'All Status' },
              { value: 'true', label: 'Active' },
              { value: 'false', label: 'Inactive' }
            ]}
            menuPortalTarget={document.body}
            styles={{
              control: (base) => ({
                ...base,
                minHeight: '38px',
                minWidth: '120px',
                fontSize: '13px',
                borderColor: '#e2e8f0',
                borderWidth: '1.5px'
              }),
              option: (base) => ({
                ...base,
                fontSize: '13px'
              }),
              menuPortal: (base) => ({ ...base, zIndex: 1050 })
            }}
          />

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontSize: '13px', color: '#718096', whiteSpace: 'nowrap' }}>Per page:</label>
            <Select
              value={{ value: pagination.limit, label: pagination.limit.toString() }}
              onChange={(option) => setPagination(prev => ({ ...prev, limit: option.value, page: 1 }))}
              options={[
                { value: 5, label: '5' },
                { value: 10, label: '10' },
                { value: 25, label: '25' },
                { value: 50, label: '50' }
              ]}
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: '38px',
                  minWidth: '80px',
                  fontSize: '13px',
                  borderColor: '#e2e8f0',
                  borderWidth: '1.5px'
                }),
                option: (base) => ({
                  ...base,
                  fontSize: '13px'
                })
              }}
            />
          </div>
        </div>

      {/* Repos List */}
      {loading ? (
        <div className="loading" style={{ textAlign: 'center', padding: '48px 24px', color: '#718096' }}>
          Loading repositories...
        </div>
      ) : repos.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 24px', background: '#f7fafc', borderRadius: '12px' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>
            {searchTerm || categoryFilter || levelFilter || activeFilter ? '🔍' : '📦'}
          </div>
          <p style={{ color: '#718096', fontSize: '15px', marginBottom: '8px' }}>
            {searchTerm || categoryFilter || levelFilter || activeFilter
              ? 'No repositories found matching your filters' 
              : 'No code repositories yet'}
          </p>
          <p style={{ color: '#718096', fontSize: '13px', marginBottom: '16px' }}>
            {searchTerm || categoryFilter || levelFilter || activeFilter
              ? 'Try adjusting your search or filters' 
              : 'Create your first code repository to get started'}
          </p>
          {(searchTerm || categoryFilter || levelFilter || activeFilter) ? (
            <button
              className="btn btn-secondary"
              onClick={() => {
                setSearchTerm('');
                setCategoryFilter('');
                setLevelFilter('');
                setActiveFilter('');
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              style={{ fontSize: '12px' }}
            >
              Clear Filters
            </button>
          ) : (
            <button
              className="btn btn-primary"
              onClick={() => setShowForm(true)}
              style={{ fontSize: '12px' }}
            >
              🚀 Create Repository
            </button>
          )}
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gap: '12px' }}>
            {repos.map(repo => {
              const levelColors = {
                beginner: { bg: '#d1fae5', color: '#065f46', border: '#9ae6b4' },
                intermediate: { bg: '#fef3c7', color: '#92400e', border: '#fbd38d' },
                advanced: { bg: '#fee2e2', color: '#991b1b', border: '#fc8181' }
              };
              const levelColor = levelColors[repo.level] || levelColors.beginner;

              return (
                <div
                  key={repo._id}
                  style={{
                    border: '1.5px solid #e2e8f0',
                    borderRadius: '12px',
                    padding: '18px',
                    background: '#fafafa',
                    transition: 'all 0.2s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'white';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#fafafa';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{ marginBottom: '12px' }}>
                    <h3 style={{ color: '#2d3748', fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>
                      {repo.title}
                    </h3>
                    {repo.description && (
                      <p style={{ color: '#718096', fontSize: '13px', marginBottom: '8px' }}>
                        {repo.description.length > 150 
                          ? repo.description.substring(0, 150) + '...' 
                          : repo.description}
                      </p>
                    )}
                    <div style={{ marginBottom: '8px', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      <span style={{ 
                        padding: '2px 8px',
                        background: 'white',
                        color: '#667eea',
                        borderRadius: '10px',
                        fontSize: '10px',
                        fontWeight: '500',
                        border: '1px solid #c7d2fe'
                      }}>
                        📋 {repo.repoCode}
                      </span>
                      <span style={{
                        padding: '2px 8px',
                        background: levelColor.bg,
                        color: levelColor.color,
                        borderRadius: '10px',
                        fontSize: '10px',
                        fontWeight: '500',
                        border: `1px solid ${levelColor.border}`,
                        textTransform: 'capitalize'
                      }}>
                        {repo.level}
                      </span>
                      {!repo.isActive && (
                        <span style={{
                          padding: '2px 8px',
                          background: '#fee2e2',
                          color: '#991b1b',
                          borderRadius: '10px',
                          fontSize: '10px',
                          fontWeight: '500',
                          border: '1px solid #fc8181'
                        }}>
                          ⚠️ Inactive
                        </span>
                      )}
                      {repo.tags && repo.tags.length > 0 && repo.tags.map((tag, index) => (
                        <span key={index} style={{ 
                          padding: '2px 8px',
                          background: '#f0fff4',
                          color: '#2f855a',
                          borderRadius: '10px',
                          fontSize: '10px',
                          fontWeight: '500',
                          border: '1px solid #9ae6b4'
                        }}>
                          🏷️ {tag.name || tag}
                        </span>
                      ))}
                    </div>
                    <p style={{ color: '#4a5568', fontSize: '12px', marginBottom: '8px' }}>
                      📝 <strong>{repo.assignmentCount || repo.assignments?.length || 0}</strong> Assignments
                      {repo.category && <> • 📂 <strong>{repo.category.name}</strong></>}
                      {repo.assignedGroups && repo.assignedGroups.length > 0 && (
                        <> • 👥 <strong>{repo.assignedGroups.length}</strong> Group(s)</>
                      )}
                    </p>
                    {(repo.startDate || repo.endDate) && (
                      <p style={{ color: '#718096', fontSize: '11px' }}>
                        🗓️ {formatDate(repo.startDate)} - {formatDate(repo.endDate)}
                      </p>
                    )}
                  </div>
                  
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleBuilder(repo._id);
                      }}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#667eea',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#5568d3'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#667eea'}
                    >
                      ⚙️ Builder
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/code-repos/${repo._id}/progress`);
                      }}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#10b981'}
                    >
                      📊 Track Progress
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(repo);
                      }}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: 'white',
                        color: '#374151',
                        border: '1.5px solid #e2e8f0',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.borderColor = '#667eea'}
                      onMouseLeave={(e) => e.target.style.borderColor = '#e2e8f0'}
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClone(repo);
                      }}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#dbeafe',
                        color: '#1e40af',
                        border: '1.5px solid #93c5fd',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#bfdbfe';
                        e.target.style.borderColor = '#60a5fa';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#dbeafe';
                        e.target.style.borderColor = '#93c5fd';
                      }}
                    >
                      📋 Clone
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(repo._id);
                      }}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#fee2e2',
                        color: '#991b1b',
                        border: '1.5px solid #fc8181',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#fca5a5';
                        e.target.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#fee2e2';
                        e.target.style.color = '#991b1b';
                      }}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination Controls */}
          {pagination.pages > 1 && (
            <div style={{ 
              marginTop: '24px', 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              padding: '16px',
              background: '#f7fafc',
              borderRadius: '8px'
            }}>
              <div style={{ fontSize: '13px', color: '#718096' }}>
                Showing {repos.length} of {pagination.total} repositories
                {(searchTerm || categoryFilter || levelFilter || activeFilter) && ` (filtered)`}
              </div>

              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                  disabled={pagination.page === 1}
                  style={{
                    padding: '8px 12px',
                    fontSize: '13px',
                    border: '1.5px solid #e2e8f0',
                    borderRadius: '6px',
                    background: pagination.page === 1 ? '#f7fafc' : 'white',
                    color: pagination.page === 1 ? '#cbd5e0' : '#2d3748',
                    cursor: pagination.page === 1 ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  ← Previous
                </button>

                <div style={{ display: 'flex', gap: '4px' }}>
                  {[...Array(pagination.pages)].map((_, index) => {
                    const pageNum = index + 1;
                    // Show first page, last page, current page, and pages around current
                    const showPage = pageNum === 1 || 
                                    pageNum === pagination.pages || 
                                    (pageNum >= pagination.page - 1 && pageNum <= pagination.page + 1);
                    
                    if (!showPage) {
                      // Show ellipsis
                      if (pageNum === pagination.page - 2 || pageNum === pagination.page + 2) {
                        return <span key={pageNum} style={{ padding: '8px 4px', color: '#cbd5e0' }}>...</span>;
                      }
                      return null;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPagination(prev => ({ ...prev, page: pageNum }))}
                        style={{
                          padding: '8px 12px',
                          fontSize: '13px',
                          border: '1.5px solid #e2e8f0',
                          borderRadius: '6px',
                          background: pagination.page === pageNum ? '#667eea' : 'white',
                          color: pagination.page === pageNum ? 'white' : '#2d3748',
                          cursor: 'pointer',
                          fontWeight: pagination.page === pageNum ? '600' : '400',
                          transition: 'all 0.2s'
                        }}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.pages, prev.page + 1) }))}
                  disabled={pagination.page === pagination.pages}
                  style={{
                    padding: '8px 12px',
                    fontSize: '13px',
                    border: '1.5px solid #e2e8f0',
                    borderRadius: '6px',
                    background: pagination.page === pagination.pages ? '#f7fafc' : 'white',
                    color: pagination.page === pagination.pages ? '#cbd5e0' : '#2d3748',
                    cursor: pagination.page === pagination.pages ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </>
      )}

      </div>
    </div>
  );
};

export default CodeRepos;
